import {makeVar, ReactiveVar} from '@apollo/client';
import type {FieldMergeFunction, StoreObject, TypePolicies} from '@apollo/client/cache';

// ============================================================================
// Type Definitions
// ============================================================================

export interface LoginInfo {
  user: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    name: string;
    email: string;
  };
  /** CWAY staff. The only gate the admin app needs; organisation-level permissions are not used here. */
  isSystemAdmin: boolean;
  /** Set to the impersonating user's username when this session is impersonated. */
  impersonatedBy?: string | null;
}

export interface UIState {
  sidebarOpen: boolean;
  sidebarMini: boolean;
  sidebarColor: string;
  sidebarBgColor: string;
  sidebarImage: string;
}

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

export interface TokenRefreshState {
  accessToken: string | null;
  refreshToken: string | null;
  isRefreshing: boolean;
  lastRefreshTime: number;
  nextRefreshTime: number | null;
  tokenExpiry: number | null;
}

// ============================================================================
// Reactive Variables
// ============================================================================

// Authentication & User State
export const loginInfoVar: ReactiveVar<LoginInfo | null> = makeVar<LoginInfo | null>(null);

// UI State
export const uiStateVar: ReactiveVar<UIState> = makeVar<UIState>({
  sidebarOpen: false,
  sidebarMini: false,
  sidebarColor: 'blue',
  sidebarBgColor: 'black',
  sidebarImage: '',
});

// Notification/Snackbar State
export const notificationVar: ReactiveVar<NotificationState> = makeVar<NotificationState>({
  open: false,
  message: '',
  severity: 'info',
});

// Filter State (for user list, org list, etc.)
export const userFilterVar: ReactiveVar<string> = makeVar<string>('');
export const orgFilterVar: ReactiveVar<string> = makeVar<string>('');

// Token Refresh State
export const tokenRefreshStateVar: ReactiveVar<TokenRefreshState> = makeVar<TokenRefreshState>({
  accessToken: null,
  refreshToken: null,
  isRefreshing: false,
  lastRefreshTime: 0,
  nextRefreshTime: null,
  tokenExpiry: null,
});

// ============================================================================
// Helper Functions
// ============================================================================

export const setLoginInfo = (info: LoginInfo | null) => {
  loginInfoVar(info);
};

export const getLoginInfo = (): LoginInfo | null => {
  return loginInfoVar();
};

export const clearLoginInfo = () => {
  loginInfoVar(null);
};

export const updateUIState = (updates: Partial<UIState>) => {
  const current = uiStateVar();
  uiStateVar({...current, ...updates});
};

export const toggleSidebar = () => {
  const current = uiStateVar();
  uiStateVar({...current, sidebarOpen: !current.sidebarOpen});
};

export const toggleSidebarMini = () => {
  const current = uiStateVar();
  uiStateVar({...current, sidebarMini: !current.sidebarMini});
};

export const showNotification = (message: string, severity: NotificationState['severity'] = 'info') => {
  notificationVar({
    open: true,
    message,
    severity,
  });
};

export const hideNotification = () => {
  notificationVar({
    ...notificationVar(),
    open: false,
  });
};

export const setUserFilter = (filter: string) => {
  userFilterVar(filter);
};

export const setOrgFilter = (filter: string) => {
  orgFilterVar(filter);
};

export const updateTokenRefreshState = (updates: Partial<TokenRefreshState>) => {
  const current = tokenRefreshStateVar();
  tokenRefreshStateVar({...current, ...updates});
};

export const setTokenRefreshing = (isRefreshing: boolean) => {
  const current = tokenRefreshStateVar();
  tokenRefreshStateVar({
    ...current,
    isRefreshing,
    lastRefreshTime: isRefreshing ? Date.now() : current.lastRefreshTime,
  });
};

export const setTokenExpiry = (expiry: number | null) => {
  const current = tokenRefreshStateVar();
  tokenRefreshStateVar({...current, tokenExpiry: expiry});
};

export const setTokens = (accessToken: string | null, refreshToken: string | null, expiry: number | null = null) => {
  const current = tokenRefreshStateVar();
  tokenRefreshStateVar({
    ...current,
    accessToken,
    refreshToken,
    tokenExpiry: expiry,
    lastRefreshTime: Date.now(),
  });
};

// ============================================================================
// Custom Hooks
// ============================================================================

import {useReactiveVar} from '@apollo/client/react';

export const useLoginInfo = () => {
  return useReactiveVar(loginInfoVar);
};

export const useUIState = () => {
  return useReactiveVar(uiStateVar);
};

export const useNotification = () => {
  return useReactiveVar(notificationVar);
};

export const useUserFilter = () => {
  return useReactiveVar(userFilterVar);
};

export const useOrgFilter = () => {
  return useReactiveVar(orgFilterVar);
};

export const useTokenRefreshState = () => {
  return useReactiveVar(tokenRefreshStateVar);
};

// ============================================================================
// Type Policies for Apollo Cache
// ============================================================================

/**
 * Field-wise merge of a subscription's product list: an incoming product takes the existing
 * fields of the product with the same id (the same position first, since one product id can
 * sit on two items at different prices). Products missing from the incoming list are gone.
 */
export const mergeProductsById: FieldMergeFunction<StoreObject[]> = (existing, incoming, {readField, mergeObjects}) => {
  if (!existing || existing.length === 0) return incoming;
  const unclaimed = new Set<StoreObject>(existing);
  return incoming.map((item, index) => {
    const id = readField<string>('id', item);
    const candidate = existing[index];
    let match = candidate && unclaimed.has(candidate) && readField<string>('id', candidate) === id ? candidate : undefined;
    if (!match) match = existing.find((row) => unclaimed.has(row) && readField<string>('id', row) === id);
    if (!match) return item;
    unclaimed.delete(match);
    return mergeObjects(match, item);
  });
};

export const localTypePolicies: TypePolicies = {
  Query: {
    fields: {
      // Removed loginInfo override - let Apollo use server data
      uiState: {
        read() {
          return uiStateVar();
        },
      },
      notification: {
        read() {
          return notificationVar();
        },
      },
      userFilter: {
        read() {
          return userFilterVar();
        },
      },
      orgFilter: {
        read() {
          return orgFilterVar();
        },
      },
      tokenRefreshState: {
        read() {
          return tokenRefreshStateVar();
        },
      },
    },
  },
  // Cache normalization: Tell Apollo how to identify unique objects
  User: {
    keyFields: ['id'], // Use 'id' field as the unique identifier
  },
  Organisation: {
    keyFields: ['id'],
  },
  Project: {
    keyFields: ['id'],
  },
  Artwork: {
    keyFields: ['id'],
  },
  File: {
    keyFields: ['uuid'], // File uses 'uuid' instead of 'id'
  },
  Team: {
    keyFields: ['id'],
  },
  UserTeam: {
    keyFields: ['id'],
  },
  // A SubscriptionProduct.id is a Stripe *product* id, not an identity: the same product
  // appears in the catalog at its default price and on each subscription with that
  // organisation's own quantity and (possibly negotiated) price. Normalising by id would
  // merge them into one cache entry and let the last write win. Keep them embedded.
  SubscriptionProduct: {
    keyFields: false,
  },
  PaymentSubscription: {
    keyFields: ['id'],
    fields: {
      // Embedded objects in a list are replaced wholesale on write. Several queries watch
      // the same subscription's products with different field sets (the organisation page,
      // the detail modal, the offers section), so each write used to drop the others'
      // fields, every watcher saw an incomplete result and refetched, and they took turns
      // forever — the page flashed. Merge per product instead, so a write only ever adds.
      products: {
        merge: mergeProductsById,
      },
    },
  },
  ModuleEntitlement: {
    keyFields: false,
  },
  SeatAssignment: {
    keyFields: false,
  },
  SeatUsage: {
    keyFields: false,
  },
};
